import { readRuntimeConfigValue } from "../../../common/runtimeConfig";
import type { ServiceOption } from "../types";

const PNOE_SERVICES_ENDPOINT = String(
  readRuntimeConfigValue("REACT_APP_PNOE_SERVICES_ENDPOINT") ||
    process.env.REACT_APP_PNOE_SERVICES_ENDPOINT ||
    "https://pnoeapi-dev.mypnoe.com/api/v2/Precision/ServiceBuilder/GetAllServices/0"
).trim();

const SERVICE_TYPE_LABELS: Record<number, string> = {
  1: "Longevity treatments",
  2: "IV Therapy",
  3: "Supplements",
  4: "Pharmaceuticals",
  5: "Pharmaceuticals IV",
  6: "Lab work",
  7: "Medical procedures",
};

type PlatformServiceRow = {
  id?: unknown;
  name?: unknown;
  serviceType?: unknown;
  description?: unknown;
  imageURL?: unknown;
  imageSmallURL?: unknown;
  active?: unknown;
  isActive?: unknown;
};

function readString(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function readServiceType(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim()) {
    const parsedValue = Number.parseInt(value, 10);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  return null;
}

function isActiveService(row: PlatformServiceRow) {
  if (typeof row.isActive === "boolean") {
    return row.isActive;
  }

  if (typeof row.active === "number") {
    return row.active > 0;
  }

  if (typeof row.active === "boolean") {
    return row.active;
  }

  return true;
}

function normalizeServiceRow(row: PlatformServiceRow): ServiceOption | null {
  if (!isActiveService(row)) {
    return null;
  }

  const id = readString(row.id);
  const label = readString(row.name);
  if (!id || !label) {
    return null;
  }

  const serviceType = readServiceType(row.serviceType);
  const category =
    (serviceType !== null && SERVICE_TYPE_LABELS[serviceType]) || "Service";
  const description = readString(row.description);
  const imageUrl = readString(row.imageSmallURL) || readString(row.imageURL);

  return {
    id,
    label,
    category,
    description: description || undefined,
    imageUrl: imageUrl || undefined,
  };
}

export const fetchClinicServices = async (): Promise<ServiceOption[]> => {
  if (!PNOE_SERVICES_ENDPOINT) {
    throw new Error("Services endpoint is not configured.");
  }

  const response = await fetch(PNOE_SERVICES_ENDPOINT, {
    headers: {
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Services request failed with status ${response.status}.`);
  }

  const data = await response.json();
  const rows = Array.isArray(data) ? data : [];
  const servicesById = new Map<string, ServiceOption>();

  rows.forEach((row) => {
    const normalizedService = normalizeServiceRow(row as PlatformServiceRow);
    if (!normalizedService) {
      return;
    }

    servicesById.set(normalizedService.id, normalizedService);
  });

  return Array.from(servicesById.values()).sort((left, right) =>
    left.label.localeCompare(right.label)
  );
};
